// src/components/ui/Button.tsx
import React, { useState, useRef, MouseEvent, AnchorHTMLAttributes, ButtonHTMLAttributes } from 'react';

// Third-party imports
import { Link } from 'react-router-dom';
import { ArrowUpRight, Loader2, LucideIcon } from 'lucide-react';

// Utility imports
import { cn } from '../../utils/cn';
import { buttonVariants, type ButtonVariants } from '../../utils/variants';

// Type definitions
type ButtonVariant = NonNullable<ButtonVariants['variant']>;
type ButtonSize = NonNullable<ButtonVariants['size']>;
type IconPosition = 'left' | 'right';

interface Ripple {
  x: number;
  y: number;
  id: number;
}

interface BaseButtonProps {
  /** The content to render inside the button or link */
  children: React.ReactNode;
  /** Visual style variant of the button */
  variant?: ButtonVariant;
  /** Button sizing scale */
  size?: ButtonSize;
  /** Additional custom Tailwind CSS classes */
  className?: string;
  /** Lucide icon or custom SVG element */
  icon?: LucideIcon | React.ReactElement | React.ComponentType<{ size?: number }>;
  /** Position of the icon relative to button text */
  iconPosition?: IconPosition;
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Displays a loading spinner and disables user interaction */
  loading?: boolean;
  /** Whether the button stretches full width of its container */
  fullWidth?: boolean;
  /** Mouse click event callback handler */
  onClick?: (e: MouseEvent<HTMLElement>) => void;
}

interface ButtonAsButton extends BaseButtonProps, Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof BaseButtonProps> {
  /** Optional URL path. If provided, component renders as link */
  href?: never;
  /** Open in new browser tab */
  external?: never;
  /** HTML button element type */
  type?: 'button' | 'submit' | 'reset';
}

interface ButtonAsLink extends BaseButtonProps, Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof BaseButtonProps> {
  /** Target URL or route path */
  href: string;
  /** Whether link opens in a new browser tab */
  external?: boolean;
  /** HTML button element type */
  type?: never;
}

type ButtonProps = ButtonAsButton | ButtonAsLink;

interface ButtonGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Buttons to group together */
  children: React.ReactNode;
  /** Additional CSS styling classes */
  className?: string;
}

const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  href,
  external,
  className = '',
  icon,
  iconPosition = 'right',
  disabled = false,
  loading = false,
  type = 'button',
  onClick,
  fullWidth = false,
  ...props
}: ButtonProps) => {
  const [ripples, setRipples] = useState<Ripple[]>([]);
  const buttonRef = useRef<HTMLButtonElement | HTMLAnchorElement>(null);

  // Adjust icon size based on button size (matching Figma design - using Tailwind classes)
  const iconSizeClasses: Record<ButtonSize, string> = {
    xs: 'w-3.5 h-3.5',  // 14px
    sm: 'w-4 h-4',      // Small: 16px
    md: 'w-5 h-5',      // Medium: 20px
    lg: 'w-6 h-6',      // Large: 24px
    xl: 'w-6 h-6',      // 24px
  };
  
  const iconSizeMap: Record<ButtonSize, number> = {
    xs: 14,
    sm: 16,  // Small: 16px
    md: 20,  // Medium: 20px
    lg: 24,  // Large: 24px
    xl: 24,
  };
  
  const iconSize = iconSizeMap[size];
  const iconSizeClass = iconSizeClasses[size];
  
  // Create the icon element based on what's provided
  // CRITICAL: Only one icon can be displayed (leading OR trailing, not both)
  const renderIcon = (): React.ReactNode | null => {
    // If loading, show spinner
    if (loading) {
      return <Loader2 size={iconSize} className={cn("animate-spin", iconSizeClass)} />;
    }
    
    // If no icon and not external, return null
    if (!icon && !external) return null;
    
    // If external link with no explicit icon, use ArrowUpRight as default (only if position is right)
    if (!icon && external && iconPosition === 'right') {
      return <ArrowUpRight size={iconSize} className={iconSizeClass} />;
    }
    
    // If icon is provided, render it
    if (icon) {
      // If icon is already a React element
      if (React.isValidElement(icon)) {
        const iconProps = icon.props as { className?: string };
        return React.cloneElement(icon, { 
          size: iconSize,
          className: cn(iconSizeClass, iconProps?.className)
        } as { size?: number; className?: string });
      }
      
      // If icon is a function component (plain function)
      if (typeof icon === 'function') {
        const IconComponent = icon as React.ComponentType<{ size?: number; className?: string }>;
        // Render the icon component as a JSX element
        // Note: Lucide icons accept both size (number) and className (string) props
        return <IconComponent size={iconSize} className={iconSizeClass} />;
      }
      
      // If icon is an object (Lucide icons are objects with render method, not plain functions)
      // Try to render it as a React component anyway - React will handle it
      if (typeof icon === 'object' && icon !== null) {
        // Cast through unknown first to avoid type errors
        const IconComponent = icon as unknown as React.ComponentType<{ size?: number; className?: string }>;
        // Render the icon component as a JSX element
        // Lucide icons are objects but can still be rendered as components
        return <IconComponent size={iconSize} className={iconSizeClass} />;
      }
      
      // If icon is something else we can't handle, return null (shouldn't happen with Lucide icons)
      console.warn('Button: Invalid icon type provided. Expected React component or element. Type:', typeof icon);
      return null;
    }
    
    return null;
  };
  
  // Ripple effect handler
  const handleRipple = (e: MouseEvent<HTMLElement>) => {
    if (disabled || loading) return;
    
    const button = buttonRef.current;
    if (!button) return;
    
    const rect = button.getBoundingClientRect();
    
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const ripple: Ripple = {
      x,
      y,
      id: Date.now(),
    };
    
    setRipples((prevRipples) => [...prevRipples, ripple]);
    
    // Remove ripple after animation
    setTimeout(() => {
      setRipples((prevRipples) => prevRipples.filter((r) => r.id !== ripple.id));
    }, 600);
  };
  
  // Combine all styles using CVA
  const buttonClasses = cn(
    buttonVariants({ variant, size }),
    fullWidth && 'w-full',
    disabled && 'opacity-50 cursor-not-allowed',
    className
  );

  // Get the icon element to render
  const iconElement = renderIcon();
  
  // Ripple elements JSX
  const rippleElements = ripples.map((ripple) => (
    <span
      key={ripple.id}
      className="absolute bg-white/30 rounded-full animate-ripple"
      style={{
        left: ripple.x,
        top: ripple.y,
        transform: 'translate(-50%, -50%)',
      }}
    />
  ));

  // Content JSX
  // CRITICAL: Only render icon in one position (leading OR trailing, not both)
  const content = (
    <>
      {rippleElements}
      {iconPosition === 'left' && iconElement}
      <span>{children}</span>
      {iconPosition === 'right' && iconElement}
    </>
  );
  
  // If href is provided, render as Link or anchor
  if (href) {
    const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
      handleRipple(e as MouseEvent<HTMLElement>);
      if (onClick) {
        onClick(e as MouseEvent<HTMLElement>);
      }

      // Handle animated page transition for internal route links
      if (!external && !isHashLink && href && !e.defaultPrevented) {
        if (typeof window !== 'undefined' && window.navigateWithTransition) {
          e.preventDefault();
          window.navigateWithTransition(href);
        }
      }
    };

    // Handle hash links with regular anchor tag (React Router Link doesn't handle hash scrolling well)
    const isHashLink = href.startsWith('#');
    
    return external || isHashLink ? (
      <a 
        href={href}
        target={external ? "_blank" : undefined}
        rel={external ? "noopener noreferrer" : undefined}
        className={buttonClasses}
        onClick={handleClick}
        ref={buttonRef as React.RefObject<HTMLAnchorElement>}
        aria-disabled={disabled || loading}
        {...(props as AnchorHTMLAttributes<HTMLAnchorElement>)}
      >
        {content}
      </a>
    ) : (
      <Link 
        to={href}
        className={buttonClasses}
        onClick={handleClick}
        ref={buttonRef as React.RefObject<HTMLAnchorElement>}
        aria-disabled={disabled || loading}
        {...(props as any)}
      >
        {content}
      </Link>
    );
  }
  
  // Otherwise render as button
  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    handleRipple(e as MouseEvent<HTMLElement>);
    onClick && onClick(e as MouseEvent<HTMLElement>);
  };

  return (
    <button
      {...(props as any)}
      type={type as 'button' | 'submit' | 'reset'}
      className={buttonClasses}
      disabled={disabled || loading}
      onClick={handleClick}
      ref={buttonRef as React.RefObject<HTMLButtonElement>}
      aria-busy={loading}
    >
      {content}
    </button>
  );
};

// Button Group component
export const ButtonGroup = ({ children, className = '', ...props }: ButtonGroupProps) => {
  return (
    <div 
      className={cn(
        'inline-flex rounded-full overflow-hidden',
        className
      )}
      role="group"
      aria-label="Button group"
      {...props}
    >
      {React.Children.map(children, (child, index) => {
        if (!React.isValidElement(child)) return child;
        
        return React.cloneElement(child, {
          className: cn(
            child.props.className,
            index === 0 && 'rounded-l-full rounded-r-none',
            index === React.Children.count(children) - 1 && 'rounded-r-full rounded-l-none',
            index !== 0 && index !== React.Children.count(children) - 1 && 'rounded-none',
            index !== 0 && 'border-l-0'
          ),
        } as any);
      })}
    </div>
  );
};

export default Button;

